/**
 * Cosa si tiene sull'apparecchio, e quando si ricontrolla.
 *
 * ## Il problema
 *
 * All'avvio l'app chiedeva **tutto**: il listino dei canali (4,91 MB su un catalogo vero), le
 * categorie, e l'EPG di ogni canale che si sfiorava. A ogni apertura, da capo. Su una linea con
 * pochi slot e un gateway che conta le richieste per indirizzo, inizializzare il catalogo è di per
 * sé un carico — e nessuno di quei dati cambia mentre si guarda la televisione.
 *
 * ## Le tre regole
 *
 * 1. **Si tiene tutto sull'apparecchio.** Il catalogo, i nomi delle categorie, le finestre di EPG
 *    già scaricate.
 * 2. **Si ricontrolla all'accesso**, e per il listino il ricontrollo costa un `ETag`: il server
 *    risponde `304` e non manda niente. Era già possibile e non si faceva, perché l'`ETag` viveva in
 *    memoria e moriva con la sessione — quindi ogni riapertura riscaricava megabyte per scoprire che
 *    erano identici.
 * 3. **Si ricontrolla ogni tot ore d'uso**, non di calendario: un apparecchio acceso tutto il giorno
 *    e uno acceso mezz'ora non hanno lo stesso bisogno. L'orologio che conta è quello del tempo
 *    passato dentro l'app.
 *
 * ## Perché le decisioni stanno qui
 *
 * Perché sono conti, e i conti si provano senza un browser. Quel che sta fuori è **leggere e
 * scrivere**; *se* rileggere, *cosa* è ancora valido e *quando* buttare via è tutto qui dentro —
 * altrimenti finisce sparso in tre `if` che al primo campo nuovo ne dimenticano uno.
 */

/**
 * La versione del formato di ciò che si archivia.
 *
 * Si alza **a ogni cambio di forma** dei dati archiviati. Senza, un'app aggiornata legge l'archivio
 * scritto dalla versione precedente e lo interpreta con il formato nuovo: il guasto che ne esce non
 * somiglia a un problema di cache, somiglia a un catalogo corrotto. Alzarla butta via tutto e
 * riscarica una volta, che è il prezzo giusto.
 *
 * `3` — i canali hanno guadagnato `logoUrl`, le categorie sono state aggiunte, e `archiveDays` è
 * diventato `archiveHours`. Quest'ultimo è il caso che rende la regola necessaria: un archivio letto
 * col nome vecchio darebbe `undefined`, cioè «niente catch-up» su ogni canale — un difetto che
 * somiglia a una configurazione sbagliata, non a una cache da buttare.
 *
 * `4` — sono arrivati film e serie, e con loro l'avanzamento di visione. Nessun formato esistente è
 * cambiato, quindi si sarebbe potuta lasciare a 3: si alza comunque perché le chiavi nuove nascono
 * insieme, e un archivio misto — canali di ieri, film di oggi — non è uno stato che qualcuno debba
 * mai dover ragionare.
 *
 * `5` — **l'ordine degli elenchi è diventato un dato.** Qui non è la forma a cambiare ma il
 * significato, ed è il caso più insidioso dei due: prima l'ordine in cui arrivavano le categorie era
 * irrilevante, perché l'app le riordinava per conto suo; adesso è `categories.position` — quel che
 * l'operatore trascina nel pannello — e l'app lo rispetta. Un archivio scritto dalla versione
 * precedente è **valido nella forma e sbagliato nel contenuto**: si legge senza un errore e mostra
 * l'ordine di prima per sei ore d'uso, cioè esattamente il difetto che questa riga corregge.
 *
 * `6` — **le categorie hanno un padre.** `ChannelCategory` ha guadagnato `parentId`, e non è un campo
 * in più: è l'albero che finalmente arriva. Un archivio scritto dalla versione precedente non ce
 * l'ha, quindi ogni categoria risulterebbe di primo livello — «Film › Azione › Spionaggio» tornerebbe
 * a essere tre voci sorelle, e per dodici ore d'uso le sottocategorie non esisterebbero. È di nuovo
 * il caso valido-nella-forma-e-sbagliato-nel-contenuto, che non dà errori e si vede solo guardando.
 *
 * La regola che ne esce, più larga di quella scritta sopra: si alza quando cambia la forma **o quando
 * cambia ciò che un campo significa**, inclusa la posizione di un elemento in un elenco.
 */
export const CACHE_VERSION = 6;

/** Le chiavi dell'archivio locale. Prefissate, per poterle buttare via tutte insieme. */
export const CACHE_KEY_PREFIX = 'palco.cache.';

export const CACHE_KEYS = {
  channels: `${CACHE_KEY_PREFIX}channels`,
  categories: `${CACHE_KEY_PREFIX}categories`,
  /** L'EPG è per finestra: la chiave la compone `epgCacheKey`. */
  epgPrefix: `${CACHE_KEY_PREFIX}epg.`,
  /**
   * L'ultimo canale guardato su **questo** apparecchio.
   *
   * Locale e non su `line.app_data`: il televisore del salotto e quello della camera erano su canali
   * diversi, e riaccendendoli devono ritrovare **il proprio**. Condividerlo li farebbe saltare a
   * vicenda, che è peggio di non ricordare niente.
   *
   * Non è una cache — non scade e non si ricontrolla — ma sta fra queste chiavi perché condivide la
   * sorte del catalogo: uscendo dall'account se ne va con lui.
   */
  lastChannel: `${CACHE_KEY_PREFIX}lastChannel`,
  /**
   * I canali preferiti, su **questo** apparecchio.
   *
   * Passavano dal gateway (`PATCH /api/v1/app/data`), e per questa versione è una complicazione che
   * non serve: la sincronia fra apparecchi è una funzione a sé, con i suoi conflitti da risolvere —
   * due televisori che tolgono e mettono lo stesso canale nello stesso momento — e non è in questo
   * giro.
   *
   * Tenerli qui toglie di mezzo un giro di rete che poteva fallire, e con esso il modo di fallire:
   * la stella si accendeva, il salvataggio non riusciva, e tornava com'era senza una parola.
   */
  favorites: `${CACHE_KEY_PREFIX}favorites`,
  /**
   * Le impostazioni di **questo** apparecchio.
   *
   * Stavano su `line.app_data`, cioè sul gateway, ed era sbagliato in due modi.
   *
   * **Nel merito:** animazioni, effetti, qualità delle immagini e ore di guida precaricata descrivono
   * *questo* apparecchio, non l'abbonamento. Il televisore vecchio del salotto ha bisogno del
   * risparmio risorse; il telefono no. Condividerle fra apparecchi significa che spegnere le
   * animazioni su un Tizen del 2017 le spegne anche sul telefono nuovo, dove non servivano — e
   * nessuno capisce perché.
   *
   * **In pratica:** ogni cambio era un giro di rete che poteva fallire, e fallendo l'interruttore
   * tornava indietro da solo. È lo stesso modo di fallire che i preferiti avevano prima di diventare
   * locali, descritto qui sopra: si tocca una voce, si vede cambiare, e torna com'era senza una
   * parola.
   *
   * **Non scade e non se ne va con l'account.** Non è una copia di qualcosa del server, ed è una
   * configurazione dell'apparecchio: chi entra dopo trova il televisore come è stato lasciato. Vale
   * anche per `adultPinEnabled`, e lì la ragione è più forte — cancellarlo all'uscita *toglierebbe*
   * una protezione proprio nel momento in cui l'apparecchio cambia mano.
   */
  settings: `${CACHE_KEY_PREFIX}settings`,
  /**
   * Le ultime ricerche fatte su questo apparecchio.
   *
   * Locali come i preferiti: sono un fatto di chi ha in mano il telecomando, non dell'abbonamento —
   * e mandare al gateway quello che una persona cerca è un dato che non abbiamo motivo di raccogliere.
   *
   * Se ne vanno con l'account, a differenza delle impostazioni: qui la ragione è l'opposto: dicono
   * cosa ha cercato chi c'era prima, e chi entra dopo non deve leggerlo.
   */
  recentSearches: `${CACHE_KEY_PREFIX}recentSearches`,
  /** Il listino dei film e quello delle serie: due chiavi, perché sono due chiamate e due schermate. */
  movies: `${CACHE_KEY_PREFIX}movies`,
  series: `${CACHE_KEY_PREFIX}series`,
  /** I nomi delle categorie VOD, separati per tipo: `get_vod_categories` e `get_series_categories`
   *  sono due elenchi diversi, e su un'installazione vera non si sovrappongono. */
  movieCategories: `${CACHE_KEY_PREFIX}movieCategories`,
  seriesCategories: `${CACHE_KEY_PREFIX}seriesCategories`,
  /**
   * Quanto si è visto di ogni film e di ogni episodio, su **questo** apparecchio.
   *
   * Locale come i preferiti e per la stessa ragione: la sincronia fra apparecchi è una funzione a sé,
   * con i suoi conflitti — due televisori che guardano lo stesso episodio insieme — e non è in questo
   * giro. La differenza è che qui il dato è più delicato: perderlo non è una comodità in meno, è
   * ricominciare un film da capo.
   *
   * **Non scade.** È un fatto di chi guarda, non una copia di qualcosa del server: le ore d'uso non
   * c'entrano, e una scadenza qui vorrebbe dire dimenticare dove si era arrivati perché sono passate
   * sei ore di televisione.
   */
  progress: `${CACHE_KEY_PREFIX}progress`,
  /** Gli ultimi titoli toccati, dal più recente: danno l'ordine di «Continua a guardare». */
  recentVod: `${CACHE_KEY_PREFIX}recentVod`,
  /**
   * «La mia lista», su **questo** apparecchio.
   *
   * Nel prototipo vive su `line.app_data`, cioè sul gateway. Qui sta accanto ai preferiti e per la
   * stessa ragione: la sincronia fra apparecchi è una funzione a sé, e chiamarla «la mia lista»
   * senza sincronizzarla prometterebbe fra apparecchi qualcosa che non avviene.
   */
  myList: `${CACHE_KEY_PREFIX}myList`,
  /**
   * I promemoria di **questo** apparecchio.
   *
   * Erano l'ultimo rimasto su `line.app_data`, cioè sul gateway: preferiti, impostazioni, «la mia
   * lista» e l'avanzamento erano già scesi quaggiù, questi no. Restavano l'unica ragione per cui
   * l'app leggeva e scriveva i dati della linea — `readLineData` tornava cinque elenchi e quattro
   * venivano buttati via all'istante.
   *
   * Le ragioni sono le stesse scritte sui preferiti, e una in più che è loro soltanto.
   *
   * **Nel merito:** un promemoria è un avviso su un apparecchio. Chi lo mette davanti alla TV del
   * salotto vuole che suoni **lì**, non sul telefono in tasca a un altro della famiglia che condivide
   * la linea. Condividerli fra apparecchi non è una comodità in più: è l'avviso che arriva a chi non
   * l'ha chiesto, mentre chi l'ha chiesto può non essere davanti a quello schermo.
   *
   * **In pratica:** ogni promemoria era un giro di rete che poteva fallire. Lo stato si aggiornava
   * subito e la differenza partiva dopo; fallendo si tornava indietro con un avviso. Un gesto da un
   * tocco che a volte si disfa da solo è peggio di uno che non c'è.
   *
   * La chiave è `canale@istante` (vedi `reminderKey`), non l'id del programma: quello cambia a ogni
   * import della guida, e un promemoria messo stasera punterebbe domani a un altro programma.
   */
  reminders: `${CACHE_KEY_PREFIX}reminders`,
  /** La scheda di una serie — stagioni ed episodi — una chiave per serie. */
  seriesDetailPrefix: `${CACHE_KEY_PREFIX}seriesDetail.`,
  /** La scheda di un film: trama, voto, durata, immagine larga. Una chiave per film. */
  movieDetailPrefix: `${CACHE_KEY_PREFIX}movieDetail.`,
  /**
   * Quanti episodi ha ogni stagione, per le serie **già aperte almeno una volta**.
   *
   * ## Perché esiste
   *
   * Il listino delle serie (`get_series`) non porta le stagioni: stanno in `get_series_info`, una
   * chiamata per serie. `watchStateOf` conta gli episodi da lì, e senza il conteggio una serie
   * guardata a metà risultava **mai iniziata** — quindi niente fascia «Continua a guardare», né in
   * home né nella vetrina on demand. L'avanzamento c'era e non lo leggeva nessuno.
   *
   * ## Perché basta, e non serve chiedere niente in più
   *
   * Su una serie si può avere un avanzamento **solo** se se ne è aperta la scheda, ed è lì che la
   * scheda arriva. L'insieme delle serie che servono a questa fascia e l'insieme di quelle già
   * chieste coincidono: l'indice si riempie da sé guardando, senza una sola richiesta aggiuntiva.
   *
   * ## Una chiave sola, non una per serie
   *
   * Le schede stanno già in `seriesDetailPrefix`, una per serie, e **scadono** dopo ventiquattro ore
   * d'uso. Questo è un riassunto di poche decine di byte per titolo che non scade: la home lo legge
   * in un colpo, mentre andare a prendere N chiavi vorrebbe dire N letture dall'archivio a ogni
   * disegno della vetrina — e una scheda scaduta si porterebbe via il conteggio con sé, cioè
   * farebbe sparire la serie da «Continua a guardare» dopo un giorno di televisione.
   */
  seriesSeasons: `${CACHE_KEY_PREFIX}seriesSeasons`,
  /**
   * Il PIN del controllo genitori: quattro cifre, su **questo** apparecchio.
   *
   * ## Non esce di qui, e non è una semplificazione
   *
   * Non va al gateway e non va al backend. Un PIN che viaggia è un PIN che qualcuno può leggere dal
   * registro di una macchina che non è in casa di chi l'ha scelto, e il valore di questa funzione sta
   * tutto nel fatto che a saperlo sia una persona sola.
   *
   * Il prezzo va detto perché è reale: **dimenticandolo non si recupera**. Non c'è nessuno a cui
   * chiederlo, e l'unica strada è uscire dall'account — che porta via anche preferiti, avanzamento e
   * impostazioni. La schermata lo scrive dove serve, cioè quando qualcuno lo sta cercando.
   *
   * ## Non se ne va con l'account, come le impostazioni
   *
   * E qui la ragione è più forte che per le impostazioni: cancellarlo all'uscita **toglierebbe una
   * protezione** proprio nel momento in cui l'apparecchio cambia mano. Se ne va solo quando lo toglie
   * chi lo ha messo, e per farlo deve digitarlo.
   */
  adultPin: `${CACHE_KEY_PREFIX}adultPin`,
} as const;

/**
 * Ogni quanto ricontrollare, in **ore d'uso**.
 *
 * Il listino e le categorie cambiano quando l'operatore importa, cioè di rado: sei ore sono
 * abbondanti, e comunque il ricontrollo del listino costa un `304`. L'EPG invece scade davvero —
 * dopo un'ora la finestra che si aveva in mano contiene programmi finiti.
 */
/**
 * `NEVER` non scade mai: si ricontrolla **solo** quando qualcuno lo chiede.
 *
 * Un numero grandissimo e non un caso a parte, perché `readCacheVerdict` confronta le ore d'uso con
 * questa soglia: un ramo in più vorrebbe dire due strade da tenere d'accordo, e una delle due prima
 * o poi non lo è più.
 */
export const NEVER_EXPIRES = Number.MAX_SAFE_INTEGER;

export const CACHE_TTL_HOURS = {
  channels: 6,
  categories: 6,
  epg: 1,
  /**
   * Film, serie e le loro categorie: **non scadono da sole**.
   *
   * Prima erano dodici ore d'uso, e ogni scadenza costava un download intero — questi elenchi non
   * hanno un `ETag` da spendere, quindi su 6824 film sono megabyte per scoprire che sono gli stessi.
   * Ma soprattutto: si riscaricavano **da soli**, in un momento che nessuno aveva chiesto, mentre il
   * tasto «Aggiorna catalogo» — l'unico gesto con cui qualcuno *chiede* di ricontrollare — non li
   * toccava affatto.
   *
   * Adesso è il contrario: si scaricano una volta, restano, e si aggiornano quando lo si chiede.
   */
  vod: NEVER_EXPIRES,
  /** La scheda di una serie: le stagioni non cambiano mentre la si guarda. */
  seriesDetail: 24,
} as const;

export interface CacheEnvelope<T> {
  /** La versione del formato con cui è stato scritto. */
  readonly v: number;
  /** Quante ore d'uso erano trascorse quando è stato scritto. */
  readonly atUsageHours: number;
  /** L'`ETag` del listino, quando la risposta ne aveva uno. */
  readonly etag?: string | null;
  readonly data: T;
}

/**
 * La chiave della guida di un canale. **Solo il canale.**
 *
 * ## L'istante d'inizio stava qui dentro, e rompeva tutto
 *
 * La chiave era `<canale>.<inizio finestra>`, e il 30-09-2026 la guida era vuota su ogni canale.
 * Chi scriveva e chi leggeva componevano la chiave con due istanti diversi: il giro di fondo scarica
 * una finestra ancorata all'**ora tonda** — così due apparecchi accesi nella stessa ora chiedono gli
 * stessi estremi e condividono la cache del backend — mentre la griglia mostra la **mezz'ora** in
 * corso. Alle 12:41 si scriveva sotto `11:00` e si leggeva sotto `12:00`: le due chiavi non si sono
 * mai incontrate.
 *
 * Nessuna prova lo vedeva perché le due metà erano provate separatamente, ognuna con la propria
 * finestra: due suite verdi su una giunzione rotta.
 *
 * E anche appaiandole sarebbe rimasto fragile: la finestra visibile si muove ogni mezz'ora, quindi
 * quel che era stato archiviato diventava irraggiungibile due volte l'ora. **Un programma appartiene
 * a un canale, non a una finestra**: l'intervallo è una domanda che si fa ai dati, non parte della
 * loro identità. Chi legge filtra per tempo.
 */
export function epgCacheKey(channelId: string): string {
  return `${CACHE_KEYS.epgPrefix}${channelId}`;
}

/** La chiave della scheda di una serie. */
export function seriesDetailCacheKey(seriesId: string): string {
  return `${CACHE_KEYS.seriesDetailPrefix}${seriesId}`;
}

/**
 * La chiave della scheda di un film.
 *
 * Un prefisso a sé e non quello delle serie: gli identificativi vengono da due tabelle diverse, e
 * anche se oggi sono UUID che non collidono, far dipendere la correttezza di un archivio dalla forma
 * degli identificativi altrui è una dipendenza che nessuno ricorderebbe il giorno in cui cambia.
 */
export function movieDetailCacheKey(movieId: string): string {
  return `${CACHE_KEYS.movieDetailPrefix}${movieId}`;
}

/**
 * Se quel che si è letto si può usare.
 *
 * Tre modi di rispondere «no», e sono diversi:
 *
 * - `absent` — non c'è niente: si scarica.
 * - `stale-version` — c'è ma è di un formato vecchio: si **butta** e si scarica. Usarlo sarebbe il
 *   guasto che non somiglia a un problema di cache.
 * - `expired` — c'è, è del formato giusto, ed è scaduto: si **tiene** e si ricontrolla. Per il
 *   listino il ricontrollo è un `ETag` che spesso risponde «identico», e in quel caso si è pagato
 *   un giro di rete invece di cinque megabyte.
 *
 * La differenza fra gli ultimi due è tutta qui: uno si può ancora mostrare mentre si aggiorna,
 * l'altro no.
 */
export type CacheVerdict<T> =
  | { readonly kind: 'fresh'; readonly data: T; readonly etag: string | null }
  | { readonly kind: 'expired'; readonly data: T; readonly etag: string | null }
  | { readonly kind: 'stale-version' }
  | { readonly kind: 'absent' };

export function readCacheVerdict<T>(
  raw: string | null,
  usageHours: number,
  ttlHours: number,
): CacheVerdict<T> {
  if (raw === null || raw === '') return { kind: 'absent' };

  let parsed: CacheEnvelope<T>;
  try {
    parsed = JSON.parse(raw) as CacheEnvelope<T>;
  } catch {
    // Un archivio illeggibile è un archivio assente: non si tenta di ripararlo, e soprattutto non si
    // lascia che un `JSON.parse` fallito butti giù l'avvio dell'app.
    return { kind: 'absent' };
  }

  if (parsed === null || typeof parsed !== 'object') return { kind: 'absent' };
  if (parsed.v !== CACHE_VERSION) return { kind: 'stale-version' };
  if (parsed.data === undefined) return { kind: 'absent' };

  const etag = parsed.etag ?? null;
  /*
    Le ore d'uso **non tornano indietro**, ma l'archivio può venire da un'installazione con un conto
    più alto. `atUsageHours` maggiore di adesso significa «scritto in un'altra vita»: si tratta come
    scaduto invece di crederci, o resterebbe valido per sempre.
  */
  const elapsed = usageHours - parsed.atUsageHours;
  if (elapsed < 0 || elapsed >= ttlHours) return { kind: 'expired', data: parsed.data, etag };
  return { kind: 'fresh', data: parsed.data, etag };
}

export function writeCacheEnvelope<T>(data: T, usageHours: number, etag: string | null = null): string {
  const envelope: CacheEnvelope<T> = { v: CACHE_VERSION, atUsageHours: usageHours, etag, data };
  return JSON.stringify(envelope);
}

/**
 * Il contatore delle **ore d'uso**.
 *
 * Non è l'orologio: un apparecchio acceso tutto il giorno e uno acceso mezz'ora non hanno lo stesso
 * bisogno di ricontrollare, e il tempo di calendario li tratterebbe uguale. Su un televisore che
 * resta in standby per giorni, un calcolo a calendario farebbe riscaricare il catalogo a ogni
 * accensione — cioè proprio il carico che si voleva togliere.
 *
 * Si accumula a passi: chi sta sopra dice ogni tanto quanti millisecondi sono passati **mentre l'app
 * era in uso**, e questa funzione li somma.
 */
export function accumulateUsageHours(storedHours: number, elapsedMs: number): number {
  if (!Number.isFinite(storedHours) || storedHours < 0) return 0;
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return storedHours;
  /*
    Un passo troppo lungo si **scarta**, non si somma.

    Fra due battiti può esserci uno standby di tre giorni: sommarlo farebbe scadere tutto di colpo al
    risveglio e riscaricare il catalogo, che è esattamente il contrario di quel che serve. Più di
    mezz'ora fra un battito e l'altro vuol dire che l'app non era in uso.
  */
  const STEP_LIMIT_MS = 30 * 60 * 1000;
  if (elapsedMs > STEP_LIMIT_MS) return storedHours;
  return storedHours + elapsedMs / 3_600_000;
}
